import { isAxiosError } from "axios";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PermissionNotice from "@/components/PermissionNotice";
import { useAnalysesByCase, useAnalysisList } from "@/hooks/useAnalysis";
import { useAuth } from "@/hooks/useAuth";
import { useCaseList } from "@/hooks/useCases";
import {
  useCreateReport,
  useDeleteReport,
  useDownloadReport,
  useReportList,
} from "@/hooks/useReports";
import { useSampleList } from "@/hooks/useSamples";
import type {
  Analysis,
  Report,
  ReportCreate,
  ReportFormat,
  ReportType,
} from "@/types";

const reportTypeLabels: Record<string, string> = {
  analysis: "분석 보고서",
  investigation: "수사 보고서",
  summary: "요약 보고서",
  forensic: "감정 보고서",
};

const formatLabels: Record<string, string> = {
  pdf: "PDF",
  json: "JSON",
  csv: "CSV",
  html: "HTML",
  pptx: "PPTX",
};

const statusLabels: Record<string, string> = {
  draft: "초안",
  generating: "생성 중",
  completed: "완료",
  failed: "실패",
};

const statusColor = (status: string) => {
  switch (status) {
    case "draft":
      return "text-gray-700 bg-gray-100";
    case "generating":
      return "text-yellow-700 bg-yellow-100";
    case "completed":
      return "text-green-700 bg-green-100";
    case "failed":
      return "text-red-700 bg-red-100";
    default:
      return "text-gray-700 bg-gray-100";
  }
};

const INITIAL_FORM: ReportCreate = {
  title: "",
  report_type: "analysis",
  format: "pdf",
  case_id: undefined,
  analysis_id: undefined,
};

/** 서버 오류를 사용자가 조치할 수 있는 한글 메시지로 변환. */
function friendlyCreateError(err: unknown): string {
  if (isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (detail === "Analysis not found")
      return "선택한 분석을 찾을 수 없습니다. 목록에서 다시 선택해 주세요.";
    if (detail === "Case not found")
      return "선택한 사건을 찾을 수 없습니다. 목록에서 다시 선택해 주세요.";
    // FastAPI 422 검증 오류는 detail 이 배열 형태
    if (Array.isArray(detail))
      return "입력값이 올바르지 않습니다. 각 항목을 확인해 주세요.";
    if (typeof detail === "string" && detail)
      return `보고서 생성에 실패했습니다: ${detail}`;
  }
  return "보고서 생성에 실패했습니다. 네트워크 또는 서버 상태를 확인해 주세요.";
}

export default function ReportsPage() {
  const { permissions } = useAuth();
  const { data: reports, isLoading, error } = useReportList();
  const createReport = useCreateReport();
  const deleteReport = useDeleteReport();
  const { download } = useDownloadReport();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ReportCreate>(INITIAL_FORM);
  const [createError, setCreateError] = useState<string | null>(null);

  // 사건·분석 목록 선택용 데이터 (ID 직접 입력 대신 드롭다운)
  const { data: cases } = useCaseList({ limit: 100 });
  // 사건 미선택: 최신 100건 / 사건 선택: 해당 사건의 전체 분석(by-case, limit 없음)
  const { data: recentAnalyses } = useAnalysisList({ limit: 100 });
  const { data: caseAnalyses } = useAnalysesByCase(form.case_id);
  const { data: samples } = useSampleList(
    form.case_id ? { case_id: form.case_id, limit: 100 } : { limit: 100 },
  );

  const sampleNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of samples ?? []) map.set(s.id, s.name);
    return map;
  }, [samples]);

  // 사건을 선택하면 해당 사건의 완료 분석만 노출 (서버 필터라 100건 윈도우 없음)
  const analysisOptions = useMemo(() => {
    const source = form.case_id ? caseAnalyses : recentAnalyses;
    return (source ?? [])
      .filter((a) => a.status === "completed")
      .sort((a, b) => b.id - a.id);
  }, [form.case_id, caseAnalyses, recentAnalyses]);

  const analysisLabel = (a: Analysis) => {
    const sampleName =
      sampleNameById.get(a.sample_id) ?? `시료 #${a.sample_id}`;
    // T1/1단계: 후보 행 수는 종 수가 아니다 — 같은 물질이 여러 스캔에 기록된다.
    const detected = a.results?.length ? ` · 후보 ${a.results.length}건` : "";
    return `분석 #${a.id} · ${sampleName}${detected}`;
  };

  const handleCreate = async () => {
    if (!form.title.trim()) {
      setCreateError("보고서 제목은 필수 입력 항목입니다.");
      return;
    }
    // 감정·분석 보고서는 분석 미연결 시 빈 보고서가 생성되므로 제출 전에 차단
    if (
      (form.report_type === "forensic" || form.report_type === "analysis") &&
      !form.analysis_id
    ) {
      setCreateError(
        "감정·분석 보고서는 분석을 연결해야 결과가 포함됩니다. '연결할 분석'에서 선택해 주세요.",
      );
      return;
    }
    setCreateError(null);
    try {
      const created = await createReport.mutateAsync({
        ...form,
        case_id: form.case_id || undefined,
        analysis_id: form.analysis_id || undefined,
      });
      // 백엔드는 콘텐츠 생성 실패도 200 + status=failed 로 반환하므로 별도 확인
      if (created?.status === "failed") {
        setCreateError(
          "보고서 생성이 실패했습니다. 연결한 분석과 출력 형식을 확인하거나 관리자에게 문의해 주세요.",
        );
        return;
      }
      setForm(INITIAL_FORM);
      setShowForm(false);
    } catch (err) {
      setCreateError(friendlyCreateError(err));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("정말 이 보고서를 삭제하시겠습니까?")) return;
    try {
      await deleteReport.mutateAsync(id);
    } catch {
      alert("삭제에 실패했습니다.");
    }
  };

  const handleDownload = async (report: Report) => {
    try {
      await download(report.id, report.file_name ?? undefined);
    } catch {
      alert("다운로드에 실패했습니다.");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">보고서 관리</h1>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-gray-200 rounded mb-3" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">보고서 관리</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 font-medium">
            데이터를 불러오는 중 오류가 발생했습니다.
          </p>
          <p className="text-red-500 text-sm mt-1">
            {(error as Error).message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">보고서 관리</h1>
        <p className="text-gray-600">보고서를 생성하고 관리할 수 있습니다.</p>
      </div>

      {/* 새 보고서 생성 */}
      {permissions?.canCreateReports ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              새 보고서 생성
            </h2>
            {!showForm && (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                + 새 보고서
              </button>
            )}
          </div>

          {showForm ? (
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      제목 *
                    </span>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) =>
                        setForm({ ...form, title: e.target.value })
                      }
                      placeholder="보고서 제목"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </label>
                </div>
                <div>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      보고서 유형
                    </span>
                    <select
                      value={form.report_type}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          report_type: e.target.value as ReportType,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="analysis">분석 보고서</option>
                      <option value="investigation">수사 보고서</option>
                      <option value="summary">요약 보고서</option>
                      <option value="forensic">감정 보고서</option>
                    </select>
                  </label>
                </div>
                <div>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      출력 형식
                    </span>
                    <select
                      value={form.format ?? "pdf"}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          format: e.target.value as ReportFormat,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="pdf">PDF</option>
                      <option value="json">JSON</option>
                      <option value="csv">CSV</option>
                      <option value="html">HTML</option>
                    </select>
                  </label>
                </div>
                <div>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      연결할 사건
                    </span>
                    <select
                      value={form.case_id ?? ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          case_id: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                          // 사건이 바뀌면 분석 필터도 바뀌므로 선택 초기화
                          analysis_id: undefined,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">사건 연결 안 함 (선택)</option>
                      {(cases ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          #{c.id} · {c.case_number} — {c.title}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      연결할 분석
                    </span>
                    <select
                      value={form.analysis_id ?? ""}
                      aria-describedby="analysis-select-help"
                      onChange={(e) =>
                        setForm({
                          ...form,
                          analysis_id: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">분석 연결 안 함 (선택)</option>
                      {analysisOptions.map((a) => (
                        <option key={a.id} value={a.id}>
                          {analysisLabel(a)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <p
                    id="analysis-select-help"
                    className="text-xs text-gray-500 mt-1"
                  >
                    완료된 분석만 표시됩니다(사건 미선택 시 최근 100건 기준).
                    사건을 먼저 선택하면 해당 사건의 분석만 보입니다.
                  </p>
                </div>
              </div>
              {createError && (
                <p role="alert" className="text-sm text-red-600 mt-4">
                  {createError}
                </p>
              )}
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setForm(INITIAL_FORM);
                    setCreateError(null);
                  }}
                  disabled={createReport.isPending}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={createReport.isPending}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {createReport.isPending ? "생성 중..." : "보고서 생성"}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              새로운 보고서를 생성하려면 "새 보고서" 버튼을 클릭하세요.
            </div>
          )}
        </div>
      ) : (
        <PermissionNotice action="보고서 생성" requiredRole="분석관" />
      )}

      {/* 보고서 목록 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">보고서 목록</h2>
          <p className="text-sm text-gray-500 mt-1">
            총 {reports?.length ?? 0}개의 보고서
          </p>
        </div>

        {reports && reports.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    제목
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    유형
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    형식
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    사건 ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    분석 ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    생성일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reports.map((report: Report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {report.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {report.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {reportTypeLabels[report.report_type] ??
                        report.report_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatLabels[report.format] ?? report.format}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColor(report.status)}`}
                      >
                        {statusLabels[report.status] ?? report.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {report.case_id ? (
                        <Link
                          to={`/cases/${report.case_id}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {report.case_id}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {report.analysis_id ? (
                        <Link
                          to={`/analyses/${report.analysis_id}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {report.analysis_id}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(report.created_at).toLocaleDateString("ko-KR")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={() => handleDownload(report)}
                          className="text-blue-600 hover:text-blue-900 hover:underline font-medium cursor-pointer"
                        >
                          다운로드
                        </button>
                        {permissions?.canDeleteReports && (
                          <button
                            type="button"
                            onClick={() => handleDelete(report.id)}
                            disabled={deleteReport.isPending}
                            className="text-red-600 hover:text-red-900 font-medium disabled:opacity-50 cursor-pointer"
                          >
                            삭제
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <p className="text-lg font-medium text-gray-900 mb-2">
              등록된 보고서가 없습니다
            </p>
            <p className="text-gray-500">
              위의 "새 보고서" 버튼으로 보고서를 생성하세요.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
